import { SITE_URL, type FaqItem } from '@/lib/jsonld';

/**
 * Growth → Eng paste for /blog/ewidencja-przychodow-ryczalt-jak-prowadzic-2026.
 * Title/H1/meta and Product VERIFY notes come from the 2026-10-07 draft.
 * Copy stays here (not i18n JSON) so locale key-parity is untouched.
 */

export const EWIDENCJA_PATH = '/blog/ewidencja-przychodow-ryczalt-jak-prowadzic-2026';
export const EWIDENCJA_TYPO_PATH = '/blog/ewidencja-przychodow-ryczalt-jak-prowadiz-2026';
export const EWIDENCJA_HTML_ENTRY = 'blog-ewidencja-przychodow-ryczalt-jak-prowadzic-2026.html';

export const EWIDENCJA_REVIEWED = '2026-10-07';
export const EWIDENCJA_PUBLISHED = '2026-03-12';

export function ewidencjaUrl(): string {
  return `${SITE_URL}${EWIDENCJA_PATH}`;
}

export const EWIDENCJA_SEO_TITLE = 'Ewidencja przychodów na ryczałcie 2026 – wzór i checklista';
export const EWIDENCJA_H1 = 'Ewidencja przychodów na ryczałcie 2026 – jak prowadzić krok po kroku';
export const EWIDENCJA_DESCRIPTION =
  'Ewidencja przychodów na ryczałcie: kolumny z rozporządzenia, jak zamknąć miesiąc, checklista. Albo prowadź automatycznie w Bookeper — 0 zł w becie.';
export const EWIDENCJA_NAV_LABEL = 'Ewidencja przychodów 2026';

export const EWIDENCJA_REGISTER_SCOPE =
  'Przychody z działalności objętej art. 15 ust. 1 wymagają ewidencji przychodów. Obowiązek nie obejmuje najmu prywatnego z art. 6 ust. 1a ani przychodów z art. 6 ust. 1d (art. 15 ust. 3).';

export const EWIDENCJA_ADVISORY_RATES =
  'Doradztwo związane z zarządzaniem (PKWiU ex dział 70), z wyjątkiem zarządzania rynkiem rybnym, inne niż w ramach wolnego zawodu: 15% (art. 12 ust. 1 pkt 2 lit. m). Doradztwo podatkowe wykonywane osobiście jako wolny zawód w rozumieniu art. 4 ust. 1 pkt 11: 17% (art. 12 ust. 1 pkt 1). Sama nazwa „doradztwo” nie wyznacza stawki.';

export const EWIDENCJA_LEAD = `Na ryczałcie z działalności nie rozliczasz kosztów jak w KPiR. ${EWIDENCJA_REGISTER_SCOPE} Dobra ewidencja odpowiada na trzy pytania: jaki przychód powstał w miesiącu, z jakiego dokumentu wynika i do czego trafi dalej (miesięczny / kwartalny ryczałt, PIT). Poniżej: minimalny proces, checklista zamknięcia miesiąca i wzór kolumn zgodny z rozporządzeniem.`;

export const EWIDENCJA_LIMIT =
  'To informacja edukacyjna, nie porada podatkowa. Przygotowujemy deklaracje na podstawie Twoich danych. Każdą sprawdzasz i zatwierdzasz przed złożeniem; obsługa kolejnych formularzy jest rozwijana. Bookeper nie zastępuje doradcy podatkowego.';

export const EWIDENCJA_PREPARE =
  'Przygotowujemy deklaracje na podstawie Twoich danych. Każdą sprawdzasz i zatwierdzasz przed złożeniem.';

export const EWIDENCJA_PRICE = 'Otwarta beta: 0 zł, bez karty, dostęp od razu';

export const EWIDENCJA_SIGNUP_ORIGIN = 'https://app.bookeper.pl/auth/sign-up';

/** Every ewidencja CTA is a JDG signup: it carries `signup_source` + `entity_type`
 *  like the hook-built CTAs, so a signup traces to the slot that sent it. */
const ewidencjaSignupHref = (utm: string, slot: string) =>
  `${EWIDENCJA_SIGNUP_ORIGIN}?${utm}&entity_type=jdg&signup_source=ewidencja_${slot}`;

export const EWIDENCJA_CTA = {
  lead: {
    label: 'Prowadź ewidencję w Bookeper — 0 zł w otwartej becie',
    href: ewidencjaSignupHref(
      'utm_source=site&utm_medium=cta&utm_campaign=ewidencja_przychodow&utm_content=lead_inline',
      'lead'
    ),
  },
  table: {
    label: 'Nie chcę tego w Excelu — załóż konto za darmo',
    href: ewidencjaSignupHref(
      'utm_source=site&utm_medium=cta&utm_campaign=ewidencja_przychodow&utm_content=wzor_table',
      'table'
    ),
    microcopy:
      'Bookeper zbiera dokumenty do ewidencji za Ciebie. Otwarta beta: 0 zł, bez karty, dostęp od razu.',
  },
  footer: {
    heading: 'Prowadź ewidencję automatycznie',
    body: 'W Bookeper wrzucasz faktury, a ewidencja i deklaracje powstają bez przepisywania Excela. Przygotowujemy deklaracje na podstawie Twoich danych — każdą sprawdzasz i zatwierdzasz przed złożeniem. Otwarta beta: 0 zł, bez karty, dostęp od razu.',
    label: 'Załóż konto za darmo',
    href: ewidencjaSignupHref(
      'utm_source=site&utm_medium=cta&utm_campaign=ewidencja_przychodow&utm_content=footer',
      'footer'
    ),
  },
  sticky: {
    line: 'Ewidencja w Bookeper — 0 zł w otwartej becie',
    label: 'Załóż konto za darmo',
    href: ewidencjaSignupHref(
      'utm_source=site&utm_medium=cta&utm_campaign=ewidencja_przychodow_sticky_cta',
      'sticky'
    ),
  },
} as const;

/** Art. 12 ust. 3 uZPD — 8.5% floor, not “highest of your own rates”. */
export const EWIDENCJA_ART12 =
  'art. 12 ust. 3 nakazuje 8,5%, albo wyższą stawkę 10–17% (ust. 1 pkt 1–4), jeśli taka kategoria jest w mieszance';

export const EWIDENCJA_CHECKLIST = [
  'Wszystkie wystawione dokumenty są wpisane',
  'Nie ma duplikatów',
  'Korekty mają jasny opis i datę oraz tę samą stawkę co korygowany dokument',
  'Suma przychodów zgadza się z podstawą do wyliczenia ryczałtu',
  `Przychody są rozdzielone per stawka (${EWIDENCJA_ART12})`,
  'Kwoty: netto u czynnego VAT-owca; kwota z dokumentu przy zwolnieniu z VAT',
] as const;

/** Statutory annex columns (Dz.U. 2025 poz. 1294) — 17 columns, including KSeF + NIP. */
export const EWIDENCJA_COLUMNS = [
  'Lp.',
  'Data wpisu',
  'Data uzyskania przychodu',
  'Nr KSeF',
  'Nr dowodu',
  'Identyfikator podatkowy kontrahenta',
  '17%',
  '15%',
  '14%',
  '12,5%',
  '12%',
  '10%',
  '8,5%',
  '5,5%',
  '3%',
  'Ogółem przychody',
  'Uwagi',
] as const;

export const EWIDENCJA_COLUMN_NOTE =
  'Wzór z załącznika: kolumna 4 to identyfikator faktury w KSeF, kolumna 5 — numer dowodu księgowego, kolumna 6 — identyfikator podatkowy kontrahenta (z kodem kraju). Kolumny 6 nie wypełnia się przy dowodzie wewnętrznym i raportach fiskalnych.';

export interface EwidencjaExampleRow {
  lp: string;
  dateIn: string;
  dateEarned: string;
  ksef: string;
  doc: string;
  nip: string;
  rate17: string;
  rate15: string;
  rate14: string;
  rate125: string;
  rate12: string;
  rate10: string;
  rate85: string;
  rate55: string;
  rate3: string;
  total: string;
  notes: string;
}

export const EWIDENCJA_ROWS: readonly EwidencjaExampleRow[] = [
  {
    lp: '1',
    dateIn: '2026-03-05',
    dateEarned: '2026-03-05',
    ksef: '1234563218-20260305-PRZYK1-01',
    doc: 'FV/3/2026',
    nip: 'PL1234563218',
    rate17: '',
    rate15: '5 000',
    rate14: '',
    rate125: '',
    rate12: '',
    rate10: '',
    rate85: '',
    rate55: '',
    rate3: '',
    total: '5 000',
    notes:
      'Doradztwo związane z zarządzaniem (PKWiU ex dział 70, poza zarządzaniem rynkiem rybnym i wolnym zawodem) — 15%, art. 12 ust. 1 pkt 2 lit. m',
  },
  {
    lp: '2',
    dateIn: '2026-03-12',
    dateEarned: '2026-03-12',
    ksef: '1234563218-20260312-PRZYK2-01',
    doc: 'FV/4/2026',
    nip: 'PL1234563218',
    rate17: '',
    rate15: '',
    rate14: '',
    rate125: '',
    rate12: '12 000',
    rate10: '',
    rate85: '',
    rate55: '',
    rate3: '',
    total: '12 000',
    notes: 'Usługa IT — PKWiU z listy 12%',
  },
  {
    lp: '3',
    dateIn: '2026-03-20',
    dateEarned: '2026-03-20',
    ksef: '1234563218-20260320-PRZYK3-01',
    doc: 'KOR/1/2026',
    nip: 'PL1234563218',
    rate17: '',
    rate15: '−500',
    rate14: '',
    rate125: '',
    rate12: '',
    rate10: '',
    rate85: '',
    rate55: '',
    rate3: '',
    total: '−500',
    notes: 'Korekta FV/3 — ta sama stawka 15%',
  },
];

export const EWIDENCJA_FAQ: readonly FaqItem[] = [
  {
    q: 'Czy na ryczałcie muszę prowadzić ewidencję przychodów?',
    a: `${EWIDENCJA_REGISTER_SCOPE} Przychody objęte obowiązkiem dokumentujesz według wzoru z Dz.U. 2025 poz. 1294 (od 1.01.2026).`,
  },
  {
    q: 'Czy Excel wystarczy?',
    a: 'Sam luźny arkusz nie zastępuje obowiązków JPK_EWP. Podatnicy z miesięcznym JPK_V7M od 2026 r. mają prowadzić ewidencję przy użyciu programu komputerowego i przekazać JPK_EWP (pierwszy raz za rok 2026 — w terminie z PIT). Pozostali ryczałtowcy: obowiązek od 2027 r. [Do potwierdzenia — JPK_EWP Excel] Nie publikujemy tu gotowego szablonu Excela ani twierdzenia, że arkusz spełnia JPK_EWP.',
  },
  {
    q: 'Czy ewidencja online jest OK?',
    a: 'Tak, jeśli masz komplet wpisów w układzie ustawowym, spójne numery dokumentów i możesz odtworzyć sumę miesiąca per stawka — oraz spełniasz wymogi JPK_EWP, gdy Cię dotyczą.',
  },
  {
    q: 'Co z różnymi stawkami ryczałtu?',
    a: `Trzymaj przychód w kolumnie właściwej stawki. Suma per stawka zasila wyliczenie ryczałtu. Bez podziału — ${EWIDENCJA_ART12}. ${EWIDENCJA_ADVISORY_RATES} Pozostałe przykłady: najem 8,5% do 100 000 zł / 12,5% powyżej; IT 12% tylko dla PKWiU z listy.`,
  },
  {
    q: 'Czy Bookeper zastępuje ewidencję?',
    a: 'Bookeper prowadzi ewidencję w aplikacji: wrzucasz dokumenty, system liczy podatki i przygotowuje deklaracje na podstawie Twoich danych. Każdą sprawdzasz i zatwierdzasz przed złożeniem; obsługa kolejnych formularzy jest rozwijana. Bookeper nie zastępuje doradcy podatkowego. W otwartej becie: 0 zł, bez karty.',
  },
];

export const EWIDENCJA_SOURCES = [
  'Rozporządzenie Ministra Finansów i Gospodarki z dnia 6 września 2025 r. w sprawie prowadzenia ewidencji przychodów (Dz.U. 2025 poz. 1294) — wzór kolumn od 1 stycznia 2026 r.',
  'Ustawa o zryczałtowanym podatku dochodowym od niektórych przychodów osiąganych przez osoby fizyczne — art. 12 ust. 3 (bez podziału: 8,5% albo wyższa stawka z ust. 1 pkt 1–4, jeśli taka kategoria jest w mieszance).',
  EWIDENCJA_REGISTER_SCOPE,
  EWIDENCJA_ADVISORY_RATES,
  'Najem: 8,5% do 100 000 zł przychodu z najmu i 12,5% powyżej; IT / oprogramowanie: 12% tylko dla PKWiU z listy ustawowej.',
] as const;

export const EWIDENCJA_DISCLAIMER =
  'Ten artykuł ma charakter informacyjny i nie stanowi porady podatkowej ani prawnej. Przepisy mogą się zmieniać. W razie wątpliwości skonsultuj się z doradcą podatkowym lub księgowym. Bookeper nie zastępuje doradcy podatkowego.';

export const EWIDENCJA_INTERNAL_LINKS = [
  { href: '/kalkulatory/ryczalt', label: 'Policz ryczałt w kalkulatorze' },
  { href: '/kalkulatory/forma-opodatkowania', label: 'Porównaj formę opodatkowania' },
  { href: '/blog/jak-przejsc-na-ryczalt-2026', label: 'Jak przejść na ryczałt w 2026' },
  { href: '/blog/pit-roczny', label: 'Roczne rozliczenie PIT' },
  { href: '/blog/rozliczenie-roczne-jdg-checklista-2026', label: 'Checklista zamknięcia roku' },
] as const;
